import { DriveConfiguration, DriveFileImport } from '../components/sync/DriveSource';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { driveSyncAPI, syncAdminAPI } from '../utils/api';
import { SyncDispatchJob, SyncTenantAccess } from '../types';

type SyncTab = 'google-drive' | 'tenant-access' | 'daemon-nodes' | 'sync-execution' | 'query-catalog';
const ALLOWED_TABS: SyncTab[] = ['google-drive', 'tenant-access', 'daemon-nodes', 'sync-execution', 'query-catalog'];
const PAGE_SIZE = 10;

function formatDate(value?: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('it-IT');
}

function stateBadgeClass(state: string): string {
  const normalized = state.toLowerCase();
  if (normalized === 'completed') return 'bg-green-100 text-green-800';
  if (normalized === 'failed') return 'bg-red-100 text-red-800';
  if (normalized === 'running' || normalized === 'pushing' || normalized === 'dispatched') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-700';
}

function formatProgress(progress?: Record<string, any>): string {
  if (!progress) return '-';
  if (progress.report) {
    const report = progress.report;
    return `${report.processed ?? 0} letti • ${report.created ?? 0} creati • ${report.updated ?? 0} aggiornati • ${report.skipped ?? 0} ignorati • ${report.errors ?? 0} errori`;
  }
  const rowsTotal = progress.rows_total ?? 0;
  const rowsSent = progress.rows_sent ?? 0;
  const rowsSkipped = progress.rows_skipped ?? 0;
  const pages = progress.pages ?? 0;
  return `pagine ${pages} • tot ${rowsTotal} • inviate ${rowsSent} • skip ${rowsSkipped}`;
}

export default function SyncAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const isHost = !!user?.is_host;
  const canManageSync = hasPermission('sync:manage');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTab: SyncTab = isHost ? 'tenant-access' : 'query-catalog';
  const currentTabParam = searchParams.get('code') || searchParams.get('error') ? 'google-drive' : searchParams.get('tab');
  const initialTab = ALLOWED_TABS.includes((currentTabParam || '') as SyncTab) ? (currentTabParam as SyncTab) : defaultTab;

  const [activeTab, setActiveTab] = useState<SyncTab>(initialTab);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(searchParams.get('tenant') || '');
  const [nodeId, setNodeId] = useState('');
  const [createdToken, setCreatedToken] = useState<{
    token: string;
    node_id: string;
    expires_at: string | null;
    mode: 'created' | 'regenerated';
  } | null>(null);
  const [nodesPage, setNodesPage] = useState(1);
  const [jobsPage, setJobsPage] = useState(1);
  const [queriesPage, setQueriesPage] = useState(1);
  const [runningQueryId, setRunningQueryId] = useState<string | null>(null);
  const [resettingQueryId, setResettingQueryId] = useState<string | null>(null);

  const setTab = (tab: SyncTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const current = searchParams.get('tab');
    const valid = ALLOWED_TABS.includes((current || '') as SyncTab) ? (current as SyncTab) : defaultTab;
    if (valid !== activeTab) setActiveTab(valid);
    if (!current) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', valid);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, defaultTab, searchParams, setSearchParams]);

  const oauthHandled = useRef(false);
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    if (oauthHandled.current || !state || (!code && !error)) return;
    oauthHandled.current = true;
    // Remove authorization data before doing other navigation or rendering links.
    setSearchParams({ tab: 'google-drive' }, { replace: true });
    setActiveTab('google-drive');
    if (error) { toast.error('Collegamento Google annullato o non autorizzato'); return; }
    driveSyncAPI.complete(code!, state).then(({ data }) => {
      setSelectedTenantId(data.tenant_id);
      queryClient.invalidateQueries({ queryKey: ['drive-config', data.tenant_id] });
      toast.success('Google collegato. Ora scegli la cartella.');
    }).catch((error) => toast.error(error?.response?.data?.detail || 'Collegamento Google non riuscito. Riprovare.'));
  }, [searchParams, setSearchParams, queryClient]);

  const tenantsQuery = useQuery({
    queryKey: ['sync-admin', 'tenants'],
    queryFn: async () => (await syncAdminAPI.listTenants({ heartbeat_window_seconds: 180 })).data.data,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!isHost && !selectedTenantId && tenantsQuery.data && tenantsQuery.data.length > 0) {
      setSelectedTenantId(tenantsQuery.data[0].tenant_id);
    }
  }, [isHost, selectedTenantId, tenantsQuery.data]);

  const selectedTenant = useMemo(
    () => tenantsQuery.data?.find((t) => t.tenant_id === selectedTenantId) ?? null,
    [tenantsQuery.data, selectedTenantId]
  );

  const nodesQuery = useQuery({
    queryKey: ['sync-admin', 'nodes', selectedTenantId, isHost, tenantsQuery.data?.map((t) => t.tenant_id)],
    queryFn: async () => {
      const tenants = (tenantsQuery.data || []).filter((tenant) =>
        selectedTenantId ? tenant.tenant_id === selectedTenantId : isHost
      );
      const nodes = await Promise.all(tenants.map(async (tenant) => {
        const response = await syncAdminAPI.listDaemonNodes(tenant.tenant_id, { heartbeat_window_seconds: 180 });
        return response.data.data.map((node) => ({ ...node, tenant_id: tenant.tenant_id, tenant_name: tenant.tenant_name }));
      }));
      return nodes.flat();
    },
    enabled: canManageSync && !!tenantsQuery.data && (isHost || !!selectedTenantId),
    refetchInterval: 30000,
  });

  const queriesQuery = useQuery({
    queryKey: ['sync-admin', 'queries', selectedTenantId],
    queryFn: async () => (await syncAdminAPI.listQueries(selectedTenantId)).data.data,
    enabled: !!selectedTenantId,
  });

  const jobsQuery = useQuery({
    queryKey: ['sync-admin', 'jobs', selectedTenantId],
    queryFn: async () => (await syncAdminAPI.listJobs(selectedTenantId, { limit: 100 })).data.data,
    enabled: !!selectedTenantId,
    refetchInterval: 5000,
  });

  useEffect(() => {
    setNodesPage(1);
    setJobsPage(1);
    setQueriesPage(1);
    setCreatedToken(null);
  }, [selectedTenantId]);

  useEffect(() => {
    if (!isHost && activeTab === 'tenant-access') setTab('daemon-nodes');
  }, [activeTab, isHost]);

  const toggleAccessMutation = useMutation({
    mutationFn: async ({ tenantId, enabled }: { tenantId: string; enabled: boolean }) =>
      syncAdminAPI.updateTenantAccess(tenantId, enabled),
    onSuccess: () => {
      toast.success('Accesso sync aggiornato');
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'tenants'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Errore aggiornamento accesso sync');
    },
  });

  const createTokenMutation = useMutation({
    mutationFn: async ({ targetNodeId }: { targetNodeId: string }) =>
      syncAdminAPI.createDaemonToken(selectedTenantId, { node_id: targetNodeId }),
    onSuccess: (res, variables) => {
      setCreatedToken({
        token: res.data.token,
        node_id: res.data.node_id,
        expires_at: res.data.expires_at,
        mode: variables.targetNodeId === nodeId.trim() ? 'created' : 'regenerated',
      });
      toast.success(variables.targetNodeId === nodeId.trim() ? 'Chiave daemon creata' : 'Chiave daemon ruotata');
      setNodeId('');
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'tenants'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Errore creazione chiave daemon');
    },
  });

  const revokeNodeMutation = useMutation({
    mutationFn: async (targetNodeId: string) => syncAdminAPI.revokeDaemonNode(selectedTenantId, targetNodeId),
    onSuccess: () => {
      toast.success('Nodo daemon revocato');
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'tenants'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Errore revoke nodo');
    },
  });

  const disableQueryMutation = useMutation({
    mutationFn: async (queryId: string) => syncAdminAPI.disableQuery(selectedTenantId, queryId),
    onSuccess: () => {
      toast.success('Query disabilitata');
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'queries', selectedTenantId] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Errore disabilitazione query');
    },
  });

  const resetCdcMutation = useMutation({
    mutationFn: async (queryId: string) => {
      setResettingQueryId(queryId);
      return syncAdminAPI.resetCdc(selectedTenantId, queryId);
    },
    onSuccess: (res, queryId) => {
      const dispatched = res.data?.dispatched ?? 0;
      toast.success(`Reset CDC per ${queryId}: ${dispatched} sync completa avviata/e`);
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'jobs', selectedTenantId] });
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'queries', selectedTenantId] });
      setTab('sync-execution');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Errore reset CDC');
    },
    onSettled: () => {
      setResettingQueryId(null);
    },
  });

  const resetCdcAllMutation = useMutation({
    mutationFn: async () => syncAdminAPI.resetCdcAll(selectedTenantId),
    onSuccess: (res) => {
      const dispatched = res.data?.dispatched ?? 0;
      toast.success(`Reset CDC tutte le entità: ${dispatched} sync completa avviata/e`);
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'jobs', selectedTenantId] });
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'queries', selectedTenantId] });
      setTab('sync-execution');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Errore reset CDC');
    },
  });

  const runQueryNowMutation = useMutation({
    mutationFn: async (queryId: string) => {
      const process = queriesQuery.data?.find(query => query.query_id === queryId);
      const activeNode = (nodesQuery.data || []).find((n) => n.is_active && (!process?.source_config?.node_id || process.source_config.node_id === n.node_id));
      if (process?.source_type !== 'google_drive' && !activeNode) throw new Error('Nessun daemon attivo disponibile');
      setRunningQueryId(queryId);
      return syncAdminAPI.dispatchJob(selectedTenantId, {
        node_id: process?.source_type === 'google_drive' ? 'google-drive' : activeNode!.node_id,
        query_id: queryId,
        command_type: 'sync',
        page_size: 200,
        max_pages: 0,
        priority: 1,
      });
    },
    onSuccess: (_res, queryId) => {
      toast.success(`Sync avviato per query ${queryId}`);
      queryClient.invalidateQueries({ queryKey: ['sync-admin', 'jobs', selectedTenantId] });
      setTab('sync-execution');
    },
    onError: (error: any) => {
      toast.error(error?.message || error?.response?.data?.detail || 'Errore avvio sync');
    },
    onSettled: () => {
      setRunningQueryId(null);
    },
  });

  const nodesData = nodesQuery.data || [];
  const jobsData = jobsQuery.data || [];
  const queriesData = queriesQuery.data || [];
  const activeNodeCount = nodesData.filter((n) => n.is_active).length;
  const daemonLimit = selectedTenant?.max_daemons ?? 1;
  const trimmedNodeId = nodeId.trim();
  const isExistingActiveNode = !!trimmedNodeId && nodesData.some((n) => n.is_active && n.node_id === trimmedNodeId);
  const limitReachedForNewNode = activeNodeCount >= daemonLimit && !isExistingActiveNode;

  const nodesTotalPages = Math.max(1, Math.ceil(nodesData.length / PAGE_SIZE));
  const jobsTotalPages = Math.max(1, Math.ceil(jobsData.length / PAGE_SIZE));
  const queriesTotalPages = Math.max(1, Math.ceil(queriesData.length / PAGE_SIZE));

  const paginatedNodes = nodesData.slice((nodesPage - 1) * PAGE_SIZE, nodesPage * PAGE_SIZE);
  const paginatedJobs = jobsData.slice((jobsPage - 1) * PAGE_SIZE, jobsPage * PAGE_SIZE);
  const paginatedQueries = queriesData.slice((queriesPage - 1) * PAGE_SIZE, queriesPage * PAGE_SIZE);

  useEffect(() => setNodesPage((prev) => Math.min(prev, nodesTotalPages)), [nodesTotalPages]);
  useEffect(() => setJobsPage((prev) => Math.min(prev, jobsTotalPages)), [jobsTotalPages]);
  useEffect(() => setQueriesPage((prev) => Math.min(prev, queriesTotalPages)), [queriesTotalPages]);

  if (!canManageSync) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Accesso negato. Richiesto ruolo admin, manager o commerciale.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sync Data Control Center</h1>
        <p className="mt-1 text-sm text-gray-500">Gestione tenant sync, daemon, Google Drive e monitoraggio esecuzioni.</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          <button onClick={() => setTab('google-drive')} className={`border-b-2 px-1 py-3 text-sm font-medium ${activeTab === 'google-drive' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500'}`}>Google Drive</button>
          {isHost && (
            <button onClick={() => setTab('tenant-access')} className={`border-b-2 px-1 py-3 text-sm font-medium ${activeTab === 'tenant-access' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
              Accesso Tenant
            </button>
          )}
          <button onClick={() => setTab('daemon-nodes')} className={`border-b-2 px-1 py-3 text-sm font-medium ${activeTab === 'daemon-nodes' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
            Daemon Nodes
          </button>
          <button onClick={() => setTab('sync-execution')} className={`border-b-2 px-1 py-3 text-sm font-medium ${activeTab === 'sync-execution' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
            Esecuzione Sync
          </button>
          <button onClick={() => setTab('query-catalog')} className={`border-b-2 px-1 py-3 text-sm font-medium ${activeTab === 'query-catalog' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
            Processi di sincronizzazione
          </button>
        </nav>
      </div>

      {isHost && activeTab !== 'tenant-access' && (
        <label className="block text-sm font-medium text-gray-700">
          Tenant
          <select value={selectedTenantId} onChange={(event) => setSelectedTenantId(event.target.value)} className="ml-3 rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">Tutti i tenant</option>
            {(tenantsQuery.data || []).map((tenant) => <option key={tenant.tenant_id} value={tenant.tenant_id}>{tenant.tenant_name}</option>)}
          </select>
        </label>
      )}

      {activeTab === 'google-drive' && (selectedTenantId ? <div key={selectedTenantId} className="space-y-4"><DriveConfiguration tenantId={selectedTenantId} /><p className="text-sm">Per configurare mapping ed esecuzioni automatiche, apri <button className="text-indigo-700 underline" onClick={() => setTab('query-catalog')}>Processi di sincronizzazione</button>.</p>{hasPermission('prodotti:edit') && <DriveFileImport tenantId={selectedTenantId} />}</div> : <p className="text-sm text-gray-600">Seleziona un tenant per configurare Google Drive.</p>)}

      {tenantsQuery.isError && <p role="alert" className="text-sm text-red-700">Impossibile caricare i tenant. Riprova aggiornando la pagina.</p>}

      {selectedTenant && (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid grid-cols-1 gap-2 text-sm text-gray-700 md:grid-cols-4">
            <div><span className="font-semibold text-gray-900">Tenant:</span> {selectedTenant.tenant_name}</div>
            <div><span className="font-semibold text-gray-900">Sync:</span> {selectedTenant.sync_enabled ? 'Abilitato' : 'Disabilitato'}</div>
            <div><span className="font-semibold text-gray-900">Daemon attivi:</span> {activeNodeCount}/{daemonLimit}</div>
            <div><span className="font-semibold text-gray-900">Ultimo heartbeat:</span> {formatDate(selectedTenant.last_heartbeat_at)}</div>
          </div>
        </section>
      )}

      {isHost && activeTab === 'tenant-access' && (
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Tenant Sync Access</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Tenant</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Sync</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Daemon</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Ultimo heartbeat</th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Azione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {(tenantsQuery.data || []).map((tenant: SyncTenantAccess) => (
                  <tr key={tenant.tenant_id} className={selectedTenantId === tenant.tenant_id ? 'bg-blue-50/40' : ''}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{tenant.tenant_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${tenant.sync_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{tenant.sync_enabled ? 'Abilitato' : 'Disabilitato'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${tenant.daemon_active ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{tenant.daemon_active ? 'Attivo' : 'Non attivo'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(tenant.last_heartbeat_at)}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => { setSelectedTenantId(tenant.tenant_id); setTab('daemon-nodes'); }} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Gestisci</button>
                        <button onClick={() => toggleAccessMutation.mutate({ tenantId: tenant.tenant_id, enabled: !tenant.sync_enabled })} className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${tenant.sync_enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>{tenant.sync_enabled ? 'Disabilita' : 'Abilita'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!selectedTenant && activeTab !== 'tenant-access' && !(isHost && activeTab === 'daemon-nodes') && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {isHost ? 'Seleziona un tenant dal filtro per gestire query ed esecuzioni.' : 'Tenant non disponibile nel contesto utente corrente.'}
        </div>
      )}

      {(selectedTenant || isHost) && activeTab === 'daemon-nodes' && (
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Daemon Nodes - {selectedTenant?.tenant_name ?? 'Tutti i tenant'}</h2>
          </div>
          <div className="space-y-4 p-4">
            {selectedTenant ? <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <input value={nodeId} onChange={(e) => setNodeId(e.target.value)} placeholder="node_id (es. edge-node-01)" className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
              <button
                onClick={() => {
                  if (!trimmedNodeId) return toast.error('Inserisci node_id');
                  if (limitReachedForNewNode) return toast.error(`Limite daemon raggiunto (${activeNodeCount}/${daemonLimit})`);
                  createTokenMutation.mutate({ targetNodeId: trimmedNodeId });
                }}
                disabled={limitReachedForNewNode}
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Crea chiave daemon
              </button>
            </div> : <p className="text-sm text-gray-600">Tutti i nodi dei tenant. Seleziona un tenant per creare o gestire le chiavi daemon.</p>}

            {createdToken && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-semibold text-blue-900">{createdToken.mode === 'regenerated' ? 'Chiave ruotata' : 'Chiave creata'} ({createdToken.node_id})</p>
                <p className="mt-1 break-all rounded border border-blue-200 bg-white p-2 font-mono text-xs text-blue-900">{createdToken.token}</p>
                <p className="mt-1 text-xs text-blue-700">Validità: {createdToken.expires_at ? formatDate(createdToken.expires_at) : 'senza scadenza, fino a revoca o rotazione'}</p>
              </div>
            )}

            {nodesQuery.isError && <p role="alert" className="text-sm text-red-700">Impossibile caricare tutti i nodi. Riprova aggiornando la pagina.</p>}
            {nodesQuery.isLoading && <p role="status" className="text-sm text-gray-500">Caricamento nodi…</p>}
            {nodesQuery.isSuccess && nodesData.length === 0 && <p className="text-sm text-gray-500">Nessun nodo configurato.</p>}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {isHost && <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Tenant</th>}
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Node</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Chiave (ultimi caratteri)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Stato</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Heartbeat</th>
                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedNodes.map((node) => (
                    <tr key={`${node.tenant_id}-${node.node_id}-${node.token_hint}`}>
                      {isHost && <td className="px-3 py-2 text-sm text-gray-900">{node.tenant_name}</td>}
                      <td className="px-3 py-2 text-sm text-gray-900">{node.node_id}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700">...{node.token_hint}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${node.daemon_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{node.daemon_active ? 'Attivo' : 'Non attivo'}</span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(node.last_heartbeat_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!selectedTenant && <button onClick={() => setSelectedTenantId(node.tenant_id)} className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Gestisci</button>}
                          {selectedTenant && node.is_active && <button onClick={() => createTokenMutation.mutate({ targetNodeId: node.node_id })} className="rounded-md border border-blue-300 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50">Ruota chiave</button>}
                          {selectedTenant && node.is_active && <button onClick={() => revokeNodeMutation.mutate(node.node_id)} className="rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700">Revoca</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {nodesTotalPages > 1 && <div className="flex items-center justify-end gap-3 text-sm">
              <button disabled={nodesPage === 1} onClick={() => setNodesPage((page) => page - 1)} className="disabled:opacity-40">Precedente</button>
              <span>Pagina {nodesPage} di {nodesTotalPages}</span>
              <button disabled={nodesPage === nodesTotalPages} onClick={() => setNodesPage((page) => page + 1)} className="disabled:opacity-40">Successiva</button>
            </div>}
          </div>
        </section>
      )}

      {selectedTenant && activeTab === 'sync-execution' && (
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Esecuzioni Sync - {selectedTenant.tenant_name}</h2>
          </div>
          <div className="space-y-4 p-4">
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Job</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Node</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Query</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Stato</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Progress</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Aggiornato</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedJobs.map((job: SyncDispatchJob) => (
                    <tr key={job.job_id}>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">{job.job_id}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{job.node_id}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{job.query_id}</td>
                      <td className="px-3 py-2 text-sm"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${stateBadgeClass(job.state)}`}>{job.state}</span></td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        <div>{formatProgress(job.progress)}</div>
                        {Array.isArray((job.progress as any)?.records) && (job.progress as any).records.length > 0 && (
                          <pre className="mt-1 max-h-24 overflow-auto rounded bg-gray-50 p-1 font-mono text-[10px] text-gray-700">{JSON.stringify((job.progress as any).records[0], null, 2)}</pre>
                        )}
                        {job.error_message && <div className="mt-1 text-red-700">{job.error_message}</div>}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(job.last_event_at || job.requested_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {selectedTenant && activeTab === 'query-catalog' && (
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Processi di sincronizzazione - {selectedTenant.tenant_name}</h2>
            <p className="mt-1 text-xs text-gray-600">Scegli la sorgente, configura il mapping e imposta quando eseguire ogni processo.</p>
          </div>
          <div className="space-y-4 p-4">
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  if (window.confirm('Reset CDC di TUTTE le entità attive e ri-sync completa? Verranno re-inviate tutte le righe (upsert idempotente, nessun duplicato).')) {
                    resetCdcAllMutation.mutate();
                  }
                }}
                disabled={resetCdcAllMutation.isPending || queriesData.length === 0}
                className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resetCdcAllMutation.isPending ? 'Reset in corso...' : 'Reset CDC tutte le entità'}
              </button>
              <Link to={`/sync-admin/query/new?tab=query-catalog&tenant=${selectedTenantId}`} className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
                Nuovo processo
              </Link>
            </div>
            <div className="space-y-3">
              {paginatedQueries.map((q) => (
                <div key={q.id} className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{q.name} <span className="font-mono text-xs text-gray-500">({q.query_id})</span></p>
                    <p className="text-xs text-gray-600">Sorgente: {q.source_type === 'as400_export' ? 'Offertify → AS400 (invio dati)' : q.source_type === 'google_drive' ? 'Google Drive (server)' : 'AS400 (demone)'} • Frequenza: ogni {q.schedule_minutes} min • Business key: {(q.business_key_fields || []).join(', ')}</p>
                    {q.schedule_window?.start && <p className="text-xs text-gray-600">Fascia: {q.schedule_window.start}–{q.schedule_window.end} ({q.schedule_window.timezone || 'Europe/Rome'})</p>}
                    {q.source_type === 'google_drive' && <p className="text-xs text-gray-600">{q.source_config?.files?.length ? `File: ${q.source_config.files.length} sorgenti${q.source_config.deduplicate_by?.length ? ' • record distinti per ' + q.source_config.deduplicate_by.join(', ') : ''}` : `File: ${q.source_config?.file_name || 'Da selezionare'}`}</p>}
                    <p className="text-xs text-gray-500">Ultima sync: {formatDate(q.last_synced_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${q.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{q.is_active ? 'Attiva' : 'Disattiva'}</span>
                    <button
                      onClick={() => q.source_type === 'as400_export' ? navigate(`/sync-admin/query/${encodeURIComponent(q.query_id)}/edit?tab=query-catalog&tenant=${selectedTenantId}`) : runQueryNowMutation.mutate(q.query_id)}
                      disabled={!q.is_active || runQueryNowMutation.isPending}
                      className="rounded-md border border-indigo-300 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {runningQueryId === q.query_id && runQueryNowMutation.isPending ? 'Esecuzione...' : q.source_type === 'as400_export' ? 'Apri invio dati' : 'Esegui sync ora'}
                    </button>
                    {q.source_type === 'as400' && <button
                      onClick={() => {
                        if (window.confirm(`Reset CDC per "${q.name}" e ri-sync completa? Verranno re-inviate TUTTE le righe di questa entità (upsert idempotente, nessun duplicato).`)) {
                          resetCdcMutation.mutate(q.query_id);
                        }
                      }}
                      disabled={!q.is_active || resetCdcMutation.isPending}
                      className="rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {resettingQueryId === q.query_id && resetCdcMutation.isPending ? 'Reset...' : 'Reset CDC'}
                    </button>}
                    <Link to={`/sync-admin/query/${encodeURIComponent(q.query_id)}/edit?tab=query-catalog&tenant=${selectedTenantId}`} className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      Modifica
                    </Link>
                    <button onClick={() => disableQueryMutation.mutate(q.query_id)} className="rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700">
                      Disabilita
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
